"use client";
import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import InputField from "../../../components/common/InputField";
import { createCourseCertificate, updateCourseCertificate, getCourseCertificatesByCourseId } from "../../../api/courseService";
import useAuth from "../../../hooks/useAuth";
import { Upload, X, Award } from "lucide-react";

interface CertificateStepProps {
  data: any;
  onChange: (data: any) => void;
  onNext: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  isEditMode?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

interface Signature {
  name: string;
  designation: string;
  image?: File | string | null;
  preview?: string;
}

interface CertificateData {
  templateID: number;
  title: string;
  subtext: string;
  description: string;
  certificateLogoPreview: string;
  certificateLogo: File | null;
  signatures: Signature[];
  studentName: string;
  date: string;
}

const templateNames = ["Premium Classic", "3D Gradient Modern"];

// Modified Premium Certificate Template Components
//logo on top
const PremiumClassicCertificate = ({
  logo,
  title,
  subtext,
  description,
  signatures,
  studentName,
  date,
  courseName,
}: any) => (
  <div className="certificate-container w-full bg-gray-50 p-4 md:p-6 rounded-xl">
    <div className="certificate-wrapper mx-auto w-full max-w-6xl">

      {/* ================= CERTIFICATE ================= */}
      <div className="certificate relative bg-white border-[3px] border-black rounded-lg shadow-2xl overflow-hidden aspect-[16/9]">

        {/* Gold Inner Border */}
        <div className="absolute inset-[5px] border border-[#d4af37] rounded-md pointer-events-none z-20" />

        {/* ================= MAIN LAYOUT ================= */}
        <div className="certificate-inner premium-certificate-inner relative z-10 h-full flex flex-col items-center text-center font-['Times_New_Roman'] px-4 md:px-10 pt-4 md:pt-6 pb-4">

          {/* ================= TOP + CENTER CONTENT ================= */}
          <div className="premium-certificate-main flex-1 min-h-0 w-full flex flex-col items-center justify-start">

            {/* LOGO */}
            <div className="premium-certificate-logo flex justify-center items-center w-[100px] h-[40px] md:w-[100px] md:h-[40px] shrink-0 mb-2">

              {logo ? (
                <img
                  src={logo}
                  alt="Certificate Logo"
                  className="max-w-full max-h-full w-auto h-auto object-contain"
                />
              ) : (
                <div className="h-14 w-14 md:h-16 md:w-16 bg-amber-100 rounded-full flex items-center justify-center border-2 border-amber-300">
                  <Award className="text-amber-600 w-9 h-9 md:w-10 md:h-10" />
                </div>
              )}

            </div>

            {/* TITLE */}
            <div className="premium-certificate-title flex flex-col items-center shrink-0 mt-2">

              <h2 className="text-2xl md:text-3xl font-bold text-[#d4af37] uppercase tracking-[0.12em] leading-tight">
                Certificate of Achievement
              </h2>

            </div>

            {/* SUBTEXT */}
            <h3 className="premium-certificate-subtext text-sm md:text-lg font-normal text-[#444] mb-2 leading-tight shrink-0">
              {subtext || "This certificate is proudly presented to"}
            </h3>

            {/* STUDENT NAME */}
            {studentName && (
              <div className="premium-certificate-student text-lg md:text-2xl font-bold text-black mb-2 px-6 py-1 border-b-2 border-t-2 border-[#d4af37] max-w-[85%] truncate shrink-0">
                {studentName}
              </div>
            )}

            {/* DESCRIPTION + COURSE NAME */}
            <div className="premium-certificate-description flex flex-wrap justify-center items-center gap-x-1 gap-y-1 text-sm md:text-base text-center text-[#333] leading-tight max-w-full px-2 mb-10">

              <span>
                {description || "for successfully completing the course"}
              </span>

              <span className="text-sm md:text-lg italic text-[#d4af37] font-medium">
                "{courseName || "Course Name"}"
              </span>

            </div>

          </div>

          {/* ================= FOOTER ================= */}
          <div className="premium-certificate-footer w-full shrink-0 grid grid-cols-3 items-end px-2 md:px-8 mt-4">

            {/* LEFT - SIGNATURE */}
            <div className="flex flex-col items-center justify-end min-w-0">

              {signatures && signatures.length > 0 && (
                <>
                  {signatures[0].preview ? (
                    <div className="premium-certificate-signature h-6 md:h-8 max-w-[120px] mb-1 mt-2 bg-white flex items-center justify-center overflow-hidden">
                      <img
                        src={signatures[0].preview}
                        alt="Signature"
                        className="h-full w-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="premium-certificate-signature h-6 md:h-8 w-24 md:w-28 bg-amber-50 rounded-md mb-1 flex items-center justify-center border-2 border-dashed border-amber-300">

                      <span className="text-[10px] md:text-xs text-amber-700 font-medium">
                        Signature
                      </span>

                    </div>
                  )}

                  <div className="premium-certificate-line w-24 md:w-28 border-t border-black" />

                  <p className="premium-certificate-label text-[10px] md:text-xs text-gray-600 mt-1">
                    Signature
                  </p>
                </>
              )}

            </div>

            {/* CENTER - DATE */}
            <div className="flex flex-col items-center justify-end min-w-0">

              <div className="premium-certificate-date text-xs md:text-sm font-semibold text-[#333] whitespace-nowrap">
                {date || ""}
              </div>

              <div className="premium-certificate-line w-24 md:w-28 border-t border-black mt-1" />

              <p className="premium-certificate-label text-[10px] md:text-xs text-gray-600 mt-1">
                Date
              </p>

            </div>

            {/* RIGHT - AUTHORIZED SIGNATORY */}
            <div className="flex flex-col items-center justify-end min-w-0">

              {signatures && signatures.length > 0 && (
                <>
                  <p className="premium-certificate-authorized font-bold text-black text-xs md:text-sm mb-0.5 text-center">
                    {signatures[0].name || "Authorized Signatory"}
                  </p>

                  <p className="premium-certificate-designation text-[10px] md:text-xs text-gray-600 text-center">
                    {signatures[0].designation || "Official Representative"}
                  </p>
                </>
              )}

            </div>

          </div>

        </div>
      </div>
    </div>
  </div>
);


const Gradient3DModernCertificate = ({
  logo,
  title,
  subtext,
  description,
  signatures,
  studentName,
  date,
  courseName,
}: any) => (
  <div className="certificate-container w-full bg-gray-50 p-4 md:p-6 rounded-xl">
    <div className="certificate-wrapper mx-auto w-full max-w-6xl">

      {/* ================= CERTIFICATE ================= */}
      <div
        className="certificate relative bg-white border-4 border-transparent rounded-lg shadow-xl overflow-hidden aspect-[16/9]"
        style={{
          borderImage: "linear-gradient(135deg, #6a11cb, #00ff88) 1",
        }}
      >

        {/* ================= BACKGROUND PATTERN ================= */}
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] pointer-events-none">
          <div className="text-[80px] font-black text-[#6a11cb] transform -rotate-12 font-['Poppins']">
          </div>
        </div>

        {/* ================= MAIN LAYOUT ================= */}
        <div className="certificate-inner gradient-certificate-inner relative z-10 h-full flex flex-col items-center text-center font-['Poppins'] px-4 md:px-10 pt-4 md:pt-6 pb-4">

          {/* ================= TOP + CENTER CONTENT ================= */}
          <div className="gradient-certificate-main flex-1 min-h-0 w-full flex flex-col items-center justify-start">

            {/* ================= TITLE ================= */}
            <div className="gradient-certificate-title flex flex-col items-center shrink-0 mb-2">

              {/* ================= LOGO - RIGHT SIDE ONLY ================= */}
              <div className="gradient-certificate-logo absolute right-2 md:right-8 top-4 md:top-6 flex items-center justify-center z-10 w-[100px] h-[50px]">

                {logo ? (
                  <img
                    src={logo}
                    alt="Certificate Logo"
                    className="max-w-full max-h-full w-auto h-auto object-contain"
                  />
                ) : (
                  <div className="w-[80px] h-[40px] bg-gradient-to-br from-[#6a11cb] to-[#00ff88] rounded-md flex items-center justify-center border-2 border-[#00ff88]">
                    <Award className="text-white w-7 h-7" />
                  </div>
                )}

              </div>

              {/* ================= CERTIFICATE TITLE ================= */}
              <h1
                className="text-3xl md:text-4xl font-bold text-center uppercase tracking-[0.12em] leading-tight"
                style={{
                  background: "linear-gradient(90deg, #6a11cb, #00ff88)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Certificate
              </h1>

              <h2
                className="text-xl md:text-2xl font-bold text-center uppercase tracking-[0.1em] leading-tight"
                style={{
                  background: "linear-gradient(90deg, #6a11cb, #00ff88)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                of Achievement
              </h2>

            </div>

            {/* ================= SUBTEXT ================= */}
            <h3 className="gradient-certificate-subtext text-sm md:text-lg font-normal text-center text-gray-600 mb-2 leading-tight shrink-0">
              {subtext || "This certificate is proudly presented to"}
            </h3>

            {/* ================= STUDENT NAME ================= */}
            {studentName && (
              <div
                className="gradient-certificate-student text-lg md:text-2xl font-bold text-gray-900 mb-2 px-6 py-1 border-b-2 border-t-2 border-[#00ff88] max-w-[85%] truncate shrink-0"
              >
                {studentName}
              </div>
            )}

            {/* ================= DESCRIPTION + COURSE NAME ================= */}
            <div className="gradient-certificate-description flex flex-wrap justify-center items-center gap-x-1 gap-y-1 text-xs md:text-base text-center text-gray-600 leading-tight max-w-full px-2">

              <span>
                {description || "for successfully completing the course"}
              </span>

              <span
                className="text-sm md:text-lg italic font-medium"
                style={{
                  color: "#6a11cb",
                }}
              >
                "{courseName || "Course Name"}"
              </span>

            </div>

          </div>

          {/* ================= FOOTER ================= */}
          <div className="gradient-certificate-footer w-full shrink-0 grid grid-cols-3 items-end px-2 md:px-8 mt-2">

            {/* ================= LEFT - SIGNATURE ================= */}
            <div className="flex flex-col items-center justify-end min-w-0">

              {signatures && signatures.length > 0 && (
                <>
                  {signatures[0].preview ? (
                    <div className="gradient-certificate-signature h-8 md:h-10 max-w-[120px] mb-1 bg-white flex items-center justify-center overflow-hidden">
                      <img
                        src={signatures[0].preview}
                        alt="Signature"
                        className="h-full w-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="relative gradient-certificate-signature h-8 md:h-10 w-24 md:w-28 border-2 border-dashed border-blue-700 rounded-md mb-1 flex items-center justify-center overflow-hidden">

                      {/* Faint gradient background only */}
                      <div className="absolute inset-0 bg-gradient-to-r from-[#6a11cb] to-[#00ff88] opacity-10" />

                      {/* Fully visible text */}
                      <span className="relative z-10 text-[10px] md:text-xs text-blue-900 font-medium">
                        Signature
                      </span>

                    </div>
                  )}

                  <div className="gradient-certificate-line w-24 md:w-28 border-t border-black" />

                  <p className="gradient-certificate-label text-[10px] md:text-xs text-gray-600 mt-1">
                    Signature
                  </p>
                </>
              )}

            </div>

            {/* ================= CENTER - DATE ================= */}
            <div className="flex flex-col items-center justify-end min-w-0">

              <div className="gradient-certificate-date text-xs md:text-sm font-semibold text-gray-600 whitespace-nowrap">
                {date || ""}
              </div>

              <div className="gradient-certificate-line w-24 md:w-28 border-t border-black mt-1" />

              <p className="gradient-certificate-label text-[10px] md:text-xs text-gray-600 mt-1">
                Date
              </p>

            </div>

            {/* ================= RIGHT - AUTHORIZED SIGNATORY */}
            <div className="flex flex-col items-center justify-end min-w-0">

              {signatures && signatures.length > 0 && (
                <>
                  <p className="gradient-certificate-authorized font-bold text-gray-900 text-xs md:text-sm mb-0.5 text-center">
                    {signatures[0].name || "Authorized Signatory"}
                  </p>

                  <p className="gradient-certificate-designation text-[10px] md:text-xs text-gray-600 text-center">
                    {signatures[0].designation || "Official Representative"}
                  </p>
                </>
              )}

            </div>

          </div>

        </div>
      </div>
    </div>
  </div>
);


const CertificateStep = forwardRef(({ 
  data, 
  onChange, 
  onNext, 
  setLoading, 
  setError,
  isEditMode = false,
  onDirtyChange
}: CertificateStepProps, ref) => {

  const { user } = useAuth();

  const [certificateEnable, setCertificateEnable] = useState(
    data.certificateEnable ?? false
  );

  const [certificate, setCertificate] = useState<CertificateData>({
    templateID: 1,
    title: "Certificate of Achievement",
    subtext: "This certificate is proudly presented to",
    description: "for successfully completing the course",
    certificateLogoPreview: "",
    certificateLogo: null,
    signatures: [
      { name: "", designation: "", image: null, preview: "" }
    ],
    studentName: user?.name || "",
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  });
  const [error,] = useState<string | null>(null);
  const [, setUserName] = useState("");
  const [originalCertificate, setOriginalCertificate] = useState<any>(null);
  const [, setIsSaving] = useState(false);

  useImperativeHandle(ref, () => ({
    saveCertificate: handleSaveCertificate,
    getCertificateEnabled: () => certificateEnable,
  }));

  useEffect(() => {
    setUserName(user?.name || "");
    setCertificate(prev => ({
      ...prev,
      studentName: user?.name || "",
      date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    }));
  }, [user]);

const courseId = data._id;
const certificates = data.certificates;

  // Fetch certificate data when component mounts or when data changes
 useEffect(() => {
  const fetchCertificateData = async () => {
    if (courseId && (!certificates || certificates.length === 0)) {
      try {
        setLoading(true);

        const response = await getCourseCertificatesByCourseId(courseId);

        console.log("========== CERTIFICATE REOPEN DEBUG ==========");
        console.log("Course ID:", courseId);
        console.log("Certificate API response:", response);
        console.log("Certificates from parent:", certificates);
        console.log("Enable flag from parent:", certificateEnable);
        console.log("================================================");

        if (
          response.status === 200 &&
          response.data &&
          response.data.length > 0
        ) {
          console.log("SAVED CERTIFICATE FOUND:", response.data[0]);

          const cert = response.data[0];

          console.log(
            "CertificateStep: Fetched certificate data",
            cert
          );

          setOriginalCertificate(cert);
          setCertificateEnable(data.certificateEnable ?? false);

          setCertificate({
            templateID: cert.templateID || 1,
            title: cert.title || "Certificate of Achievement",
            subtext:
              cert.subtext ||
              "This certificate is proudly presented to",
            description:
              cert.description ||
              "for successfully completing the course",

            certificateLogoPreview:
              cert.certificateLogo || "",

            certificateLogo: null,

            signatures:
              cert.signature?.map((sig: any) => ({
                name: sig.name || "",
                designation: sig.designation || "",
                preview: sig.image || "",
                image: null,
              })) || [
                {
                  name: "",
                  designation: "",
                  image: null,
                  preview: "",
                },
              ],

            studentName: user?.name || "",

            date: new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            }),
          });

          onChange({
            ...data,
            certificates: [cert],
            certificateEnable: data.certificateEnable ?? false,
          });
        }
      } catch (error) {
        console.error("Error fetching certificate data:", error);
      } finally {
        setLoading(false);
      }
      } else if (
        data.certificateEnable &&
        certificates &&
        certificates.length > 0
      ) {
        const cert = certificates[0];

      console.log(
        "CertificateStep: Using existing certificate data",
        cert
      );

      setOriginalCertificate(cert);
      setCertificateEnable(data.certificateEnable ?? false);      

      setCertificate({
        templateID: cert.templateID || 1,
        title: cert.title || "Certificate of Achievement",
        subtext:
          cert.subtext ||
          "This certificate is proudly presented to",
        description:
          cert.description ||
          "for successfully completing the course",

        certificateLogoPreview:
          cert.certificateLogo || "",

        certificateLogo: null,

        signatures:
          cert.signature?.map((sig: any) => ({
            name: sig.name || "",
            designation: sig.designation || "",
            preview: sig.image || "",
            image: null,
          })) || [
            {
              name: "",
              designation: "",
              image: null,
              preview: "",
            },
          ],

        studentName: user?.name || "",

        date: new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      });
    } else if (certificateEnable !== undefined) {
      console.log(
        "CertificateStep: Using certificateEnable flag",
        certificateEnable
      );

      setCertificateEnable(certificateEnable);

      setCertificate((prev) => ({
        ...prev,
        studentName: user?.name || "",
      }));
    }
  };

  fetchCertificateData();
}, [
  courseId,
  certificates,
  certificateEnable,
  onChange,
  setLoading,
]);

useEffect(() => {
  if (data.certificateEnable !== undefined) {
    setCertificateEnable(data.certificateEnable);
  }
}, [data.certificateEnable]);


  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.match('image.*')) {
        setError("Only image files are supported");
        return;
      }
      setCertificate({
        ...certificate,
        certificateLogo: file,
        certificateLogoPreview: URL.createObjectURL(file)
      });
      markCertificateDirty();
      console.log("CertificateStep: Logo uploaded", file);
    }
  };

  const removeLogo = () => {
    setCertificate({
      ...certificate,
      certificateLogo: null,
      certificateLogoPreview: ""
    });
    markCertificateDirty();
    console.log("CertificateStep: Logo removed");
  };

  const handleSignatureUpload = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      console.log("========== SIGNATURE FILE DEBUG ==========");
      console.log("File name:", file.name);
      console.log("File type:", file.type);
      console.log("File size:", file.size);
      console.log("Object URL:", URL.createObjectURL(file));
      console.log("==========================================");
      
      if (!file.type.match('image.*')) {
        setError("Only image files are supported");
        return;
      }
      const newSignatures = [...certificate.signatures];
      newSignatures[index] = {
        ...newSignatures[index],
        image: file,
        preview: URL.createObjectURL(file)
      };
      setCertificate({
        ...certificate,
        signatures: newSignatures
      });
      markCertificateDirty();
      console.log(`CertificateStep: Signature ${index} uploaded`, file);
      console.log("CertificateStep: Updated signatures with image", newSignatures);
    }
  };

  const removeSignature = (index: number) => {
    const newSignatures = [...certificate.signatures];
    newSignatures[index] = { ...newSignatures[index], image: null, preview: "" };
    setCertificate({ ...certificate, signatures: newSignatures });
    markCertificateDirty();
    console.log(`CertificateStep: Signature ${index} removed`);
    console.log("CertificateStep: Updated signatures after removal", newSignatures);
  };

  const handleSignatureChange = (index: number, field: 'name' | 'designation', value: string) => {
    const newSignatures = [...certificate.signatures];
    newSignatures[index] = { ...newSignatures[index], [field]: value };
    setCertificate({ ...certificate, signatures: newSignatures });
    markCertificateDirty();
    console.log(`CertificateStep: Signature ${index} ${field} changed to`, value);
    console.log("CertificateStep: Updated signatures", newSignatures);
  };

  const handleSaveCertificate = async () => {
  try {
    setIsSaving(true);
    setLoading(true);
    setError(null);

    console.log("CertificateStep: Saving certificate", certificate);

    // Keep certificate enabled/disabled state in parent
    onChange({
      ...data,
      certificateEnable: certificateEnable,
    });

    // =========================================================
    // CERTIFICATE DISABLED
    // =========================================================
    if (!certificateEnable) {
      onChange({
        ...data,
        certificateEnable: false,
      });

      return false; // No need to save if certificate is disabled
    }

    // =========================================================
    // VALIDATION
    // =========================================================
    if (!certificate.title) {
      setError("Certificate title is required");
      return;
    }

    if (
      !certificate.certificateLogo &&
      !certificate.certificateLogoPreview &&
      !data.certificates?.length
    ) {
      setError("Please upload a certificate logo");
      return;
    }

    const signature = certificate.signatures[0];

    if (!signature.name) {
      setError("Signature name is required");
      return;
    }

    if (!signature.designation) {
      setError("Signature designation is required");
      return;
    }

    if (
      !signature.image &&
      !signature.preview &&
      !data.certificates?.length
    ) {
      setError("Signature image is required");
      return;
    }

    if (!user?._id) {
      setError("User authentication failed");
      return;
    }

    // =========================================================
    // PREPARE SIGNATURE DATA
    // =========================================================
    const signatureData = {
      name: signature.name || "Instructor",
      designation: signature.designation || "Instructor",
    };

    console.log("CertificateStep: Prepared signature data", signatureData);

    let response;

    // =========================================================
    // UPDATE EXISTING CERTIFICATE
    // =========================================================
    if (data.certificates && data.certificates.length > 0) {
      const updateData: any = {
        id: data.certificates[0]._id,
        // Always send the current certificate text/details
        title: certificate.title,
        subtext: certificate.subtext,
        description: certificate.description,
        user_id: user._id,
        signature: signatureData,
      };

      // ---------------------------------------------------------
      // NEW LOGO
      // Only send this when user selected a new logo.
      // ---------------------------------------------------------
      if (certificate.certificateLogo) {
        updateData.certificateLogo = certificate.certificateLogo;
      }

      // ---------------------------------------------------------
      // NEW SIGNATURE IMAGE
      // Only send this when user selected a new signature image.
      // ---------------------------------------------------------
      if (signature.image) {
        updateData.signatureFile = signature.image;
      }

      console.log(
        "CertificateStep: Final update data",
        updateData
      );

      response = await updateCourseCertificate(updateData);
    }

    // =========================================================
    // CREATE NEW CERTIFICATE
    // =========================================================
    else {
      response = await createCourseCertificate({
        courseId: data._id,
        templateID: certificate.templateID,
        title: certificate.title,
        subtext: certificate.subtext,
        description: certificate.description,
        certificateLogo: certificate.certificateLogo as File,
        signature: signatureData,
        signatureFile: signature.image as File,
        createdBy: user._id,
      });
    }

    // =========================================================
    // API RESPONSE
    // =========================================================
    console.log(
      "CertificateStep: API response",
      response
    );

    if (!response || response.status >= 400) {
      setError(
        response?.message || "Failed to save certificate"
      );
      return;
    }

    // UPDATE PARENT STATE WITH SAVED DATA
    const updatedData = {
      ...data,
      certificates: response.data
        ? [response.data]
        : data.certificates,
      certificateEnable: certificateEnable,
    };

    onChange(updatedData);

    // UPDATE ORIGINAL CERTIFICATE
    // So the next save correctly knows what is currently saved.
    if (response.data) {
      setOriginalCertificate(response.data);
    }

    // Certificate is now saved
    onDirtyChange?.(false);

    console.log(
      "CertificateStep: Certificate saved successfully"
    );

    return true;

  } catch (err) {
    console.error(
      "CertificateStep: Error saving certificate",
      err
    );

    setError(
      err instanceof Error
        ? err.message
        : "Failed to save certificate. Please try again."
    );
  } finally {
    setLoading(false);
    setIsSaving(false);
  }
};

  const renderCertificatePreview = () => {
    // Ensure signatures data is properly structured
    const formattedSignatures = certificate.signatures.map(sig => ({
      name: sig.name || "",
      designation: sig.designation || "",
      preview: sig.preview || "",
      image: sig.image || null
    }));
    
    console.log("CertificateStep: Rendering preview with signatures", formattedSignatures);
    
    const props = {
      logo: certificate.certificateLogoPreview,
      title: certificate.title,
      subtext: certificate.subtext,
      description: certificate.description,
      signatures: formattedSignatures,
      studentName: certificate.studentName,
      date: certificate.date,
      courseName: data.title // Pass the course name
    };
    
    switch (certificate.templateID) {
      case 1: return <PremiumClassicCertificate {...props} />;
      case 2: return <Gradient3DModernCertificate {...props} />;
      default: return <PremiumClassicCertificate {...props} />;
    }
  };

  const markCertificateDirty = () => {
  onDirtyChange?.(true);
};

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-6">
        <div className="flex items-center mb-3">
          <div className="bg-purple-100 rounded-full p-2 mr-3">
            <Award className="text-purple-600 w-6 h-6" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Award Completion Certificates</h2>
        </div>
        <p className="text-gray-600">Create beautiful certificates to recognize student achievement and course completion.</p>
      </div>
      
      {/* Enable Certificate Toggle */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Enable Certificate for this Course</h3>
            <p className="text-sm text-gray-600">Students will receive a beautiful certificate upon successful course completion.</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={certificateEnable}
              onChange={(e) => {
                const enabled = e.target.checked;

                console.log("CertificateStep: Toggle certificate", enabled);

                setCertificateEnable(enabled);

                onChange({
                  ...data,
                  certificateEnable: enabled
                });

                onDirtyChange?.(true);
              }}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
          </label>
        </div>
      </div>
      
      {certificateEnable && (
        <>
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
            <h3 className="text-base md:text-lg font-medium text-gray-900 mb-3 md:mb-4">Select Certificate Template</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
              {[1, 2].map((templateId) => (
                <div
                  key={templateId}
                  className={`border-2 rounded-lg p-2 md:p-3 cursor-pointer transition-all ${
                    certificate.templateID === templateId 
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-100' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => {
                    console.log("CertificateStep: Selected template", templateId);
                    setCertificate({ ...certificate, templateID: templateId });
                    markCertificateDirty();
                  }}
                >
                  <div className="aspect-[16/9] bg-gray-100 rounded mb-2 flex items-center justify-center">
                    <div className="text-xs text-gray-500">{templateNames[templateId - 1]}</div>
                  </div>
                  <p className="text-xs text-center font-medium">
                    {templateNames[templateId - 1]}
                  </p>
                </div>
              ))}
            </div>
            
            <div className="border rounded-lg p-3 md:p-4 bg-gray-50 mt-4">
              <h4 className="text-sm md:text-md font-medium text-gray-900 mb-3 md:mb-4 text-center">
                {templateNames[certificate.templateID - 1]} Preview
              </h4>
              <div className="w-full overflow-x-auto">
                <div className="flex justify-center min-w-full">
                  <div className="w-full max-w-6xl mx-auto">
                    {renderCertificatePreview()}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
            <h3 className="text-base md:text-lg font-medium text-gray-900 mb-3 md:mb-4">Certificate Details</h3>
            <div className="space-y-3 md:space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">Certificate Title</label>
                <InputField
                  type="text"
                  value={certificate.title}
                  onChange={(e) => {
                    console.log("CertificateStep: Title changed", e.target.value);
                    setCertificate({ ...certificate, title: e.target.value });
                    markCertificateDirty();
                  }}
                  placeholder="Certificate of Achievement"
                  name="title"
                  label=""
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">Certificate Subtext</label>
                <InputField
                  type="text"
                  value={certificate.subtext}
                  onChange={(e) => {
                    console.log("CertificateStep: Subtext changed", e.target.value);
                    setCertificate({ ...certificate, subtext: e.target.value });
                    markCertificateDirty();
                  }}
                  placeholder="This certificate is proudly presented to"
                  name="subtext"
                  label=""
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">Certificate Description</label>
                <textarea
                  value={certificate.description}
                  onChange={(e) => {
                    console.log("CertificateStep: Description changed", e.target.value);
                    setCertificate({ ...certificate, description: e.target.value });
                    markCertificateDirty();
                  }}
                  placeholder="for successfully completing the course"
                  rows={3}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm md:text-base"
                />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
            <h3 className="text-base md:text-lg font-medium text-gray-900 mb-3 md:mb-4">Certificate Logo</h3>
            <div className="mt-1">
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                id="certificate-logo-upload"
              />
              <label htmlFor="certificate-logo-upload" className="block cursor-pointer">
                <div className="relative aspect-video w-full max-w-md overflow-hidden rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-400 transition-all duration-200 bg-gradient-to-br from-gray-50 to-blue-50 hover:from-blue-50 hover:to-indigo-50">
                  {certificate.certificateLogoPreview ? (
                    <>
                      <img
                        src={certificate.certificateLogoPreview}
                        alt="Logo Preview"
                        className="absolute inset-0 w-full h-full object-contain bg-white p-4 rounded-xl"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          removeLogo();
                        }}
                        className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-lg text-red-600 hover:bg-red-50 transition-colors duration-200 border border-red-200"
                      >
                        <X className="h-4 w-4 md:h-5 md:w-5" />
                      </button>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-600 p-4">
                      <div className="bg-blue-100 rounded-full p-3 mb-3">
                        <Upload className="h-6 w-6 md:h-8 md:w-8 text-blue-600" />
                      </div>
                      <span className="text-sm md:text-base font-semibold mb-1">Upload Certificate Logo</span>
                      <span className="text-xs md:text-sm text-gray-500">PNG, JPG up to 10MB</span>
                      <span className="text-xs text-gray-400 mt-1">Recommended: Square or 1:1 aspect ratio</span>
                    </div>
                  )}
                </div>
              </label>
            </div>
          </div>
          
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
            <h3 className="text-base md:text-lg font-medium text-gray-900 mb-3 md:mb-4">Signature</h3>
            
            <div className="border rounded-lg p-3 md:p-4 bg-white mb-3 md:mb-4 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4">
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500"></span>
                  </label>
                  <InputField
                    type="text"
                    value={certificate.signatures[0].name}
                    onChange={(e) => handleSignatureChange(0, 'name', e.target.value)}
                    placeholder="Enter name"
                    name="signature-name"
                    label=""
                    className="text-sm md:text-base"
                  />
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    Designation <span className="text-red-500"></span>
                  </label>
                  <InputField
                    type="text"
                    value={certificate.signatures[0].designation}
                    onChange={(e) => handleSignatureChange(0, 'designation', e.target.value)}
                    placeholder="Enter designation"
                    name="signature-designation"
                    label=""
                    className="text-sm md:text-base"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  Signature Image <span className="text-red-500"></span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleSignatureUpload(0, e)}
                  className="hidden"
                  id="signature-upload"
                />
                <label htmlFor="signature-upload" className="block cursor-pointer">
                  <div className="relative aspect-video w-full max-w-md border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 transition-all duration-200 bg-gradient-to-br from-gray-50 to-blue-50 hover:from-blue-50 hover:to-indigo-50">
                    {certificate.signatures[0].preview ? (
                      <>
                        <img
                          src={certificate.signatures[0].preview}
                          alt="Signature Preview"
                          className="absolute inset-0 w-full h-full object-contain p-3 md:p-4 bg-white rounded-xl"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            removeSignature(0);
                          }}
                          className="absolute top-1 md:top-2 left-1 md:left-2 p-1.5 bg-white rounded-full shadow-lg text-red-600 hover:bg-red-50 transition-colors duration-200 border border-red-200"
                        >
                          <X className="h-3 w-3 md:h-4 md:w-4" />
                        </button>
                      </>
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-600 p-3 md:p-4">
                        <div className="bg-blue-100 rounded-full p-2 mb-2">
                          <Upload className="h-5 w-5 md:h-6 md:w-6 text-blue-600" />
                        </div>
                        <span className="text-xs md:text-sm font-semibold mb-1">Upload Signature</span>
                        <span className="text-[10px] md:text-xs text-gray-500">PNG, JPG files only</span>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>
          </div>
          
          {error && (
            <div className="p-3 md:p-4 bg-red-50 rounded-lg">
              <p className="text-red-600 text-xs md:text-sm">{error}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
});

export default CertificateStep;